import type { TemplateOut } from "@sagot/api-client";
import { TEMPLATE_ICONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, RadioGroup, Skeleton, type RadioOption } from "@sagot/ui";

interface TemplatePickerProps {
  /** Already filtered + ranked for the chosen industry and goals (see `rankTemplates`). */
  templates: readonly TemplateOut[] | undefined;
  isPending: boolean;
  isError: boolean;
  value: string;
  recommended: string | undefined;
  /** Templates are matched to the industry, so nothing can be chosen before one is picked. */
  disabled?: boolean;
  onChange: (key: string) => void;
  onRetry: () => void;
}

/**
 * Single-select template cards (radio group: one tab stop, arrow keys). The checked card is the
 * selection; the best match for industry + goals carries the "Recommended" badge.
 */
export function TemplatePicker({ templates, isPending, isError, value, recommended, disabled = false, onChange, onRetry }: TemplatePickerProps) {
  const { t } = useTranslation();
  if (isPending) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-[84px] rounded-lg" />
        ))}
      </div>
    );
  }
  if (isError || !templates) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm text-warning" role="alert">
        <p>{t("onboarding.org.templatesError")}</p>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          <Icon name="refresh-cw" />
          {t("onboarding.org.retry")}
        </Button>
      </div>
    );
  }
  const options: RadioOption<string>[] = templates.map((template) => ({
    value: template.key,
    label: (
      <span data-template={template.key} className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-sunken text-fg-muted">
          <Icon name={TEMPLATE_ICONS[template.key] ?? "sparkles"} className="size-3.5" />
        </span>
        {template.name}
        {template.key === recommended ? <Badge tone="brand">{t("onboarding.org.recommended")}</Badge> : null}
      </span>
    ),
    description: template.description ?? undefined,
  }));
  return (
    <RadioGroup<string>
      aria-label={t("onboarding.org.templatesLabel")}
      variant="cards"
      className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="template-picker"
      options={options}
      value={disabled ? "" : value}
      disabled={disabled}
      onValueChange={onChange}
    />
  );
}
